import React, { useCallback, useEffect, useRef, useState } from 'react'
import './Toast.css'

const ICONS = {
  success: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 8v5.5M12 16.5h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 11v5.5M12 8h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 9v4.5M12 16.5h.01M10.3 4.3 2.6 18a2 2 0 0 0 1.75 3h15.3a2 2 0 0 0 1.75-3L13.7 4.3a2 2 0 0 0-3.4 0Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
}

const LABELS = {
  success: 'Success',
  error: 'Error',
  info: 'Info',
  warning: 'Warning',
}

const Toast = ({ message, type = 'success', duration = 3000, onClose }) => {
  const tone = ICONS[type] ? type : 'info'
  const [phase, setPhase] = useState('enter') // enter | shown | exit
  const [paused, setPaused] = useState(false)
  const remainingRef = useRef(duration)
  const startedAtRef = useRef(Date.now())
  const timerRef = useRef(null)
  const exitTimerRef = useRef(null)

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setPhase('exit')
    exitTimerRef.current = setTimeout(() => {
      onClose?.()
    }, 340)
  }, [onClose])

  useEffect(() => {
    const frame = requestAnimationFrame(() => setPhase('shown'))
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    if (paused || phase === 'exit') return undefined

    startedAtRef.current = Date.now()
    timerRef.current = setTimeout(dismiss, remainingRef.current)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [paused, phase, dismiss])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
  }, [])

  const onMouseEnter = () => {
    if (phase === 'exit') return
    const elapsed = Date.now() - startedAtRef.current
    remainingRef.current = Math.max(400, remainingRef.current - elapsed)
    setPaused(true)
  }

  const onMouseLeave = () => {
    if (phase === 'exit') return
    setPaused(false)
  }

  return (
    <div
      className={`ex-toast ex-toast--${tone} ex-toast--${phase}${paused ? ' is-paused' : ''}`}
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="ex-toast__glow" aria-hidden="true" />
      <div className="ex-toast__body">
        <div className="ex-toast__icon" aria-hidden="true">
          {ICONS[tone]}
        </div>
        <div className="ex-toast__copy">
          <span className="ex-toast__label">{LABELS[tone]}</span>
          <p className="ex-toast__message">{message}</p>
        </div>
        <button
          type="button"
          className="ex-toast__close"
          aria-label="Dismiss notification"
          onClick={dismiss}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="ex-toast__progress" aria-hidden="true">
        <span
          className="ex-toast__progress-bar"
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      </div>
    </div>
  )
}

export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="ex-toast-stack" aria-label="Notifications">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  )
}

export default Toast
